import React, { useState, useEffect } from 'react';
import DirectoryList from '../../shared/directoryList/components/DirectoryListPage';
import ManageSlideshowsModal from './ManageSlideshowsModal';
import AddImageModal from './AddImageModal';
import { getSlideshows, updateSlideshow, addImage } from '../api/slideShowApi';
import { v4 as uuid } from 'uuid';

const SlideShowListPage = () => {
  // State for slideshows and images
  const [slideshows, setSlideshows] = useState({});
  const [slideshowData, setSlideshowData] = useState({});
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for managing the modals
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedSlideshow, setSelectedSlideshow] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageEditMode, setImageEditMode] = useState('add'); // 'add' or 'edit'

  // Fetch slideshows from the API
  const fetchSlideshows = async () => {
    setLoading(true);
    try {
      const data = await getSlideshows();
      const slideshows = data?.record?.data?.items || {};
      setSlideshows(slideshows);
      setSlideshowData(data?.record || {});
      const images = Object.values(slideshows).flatMap(
        (imageObj) => Object.values(imageObj?.items || {}) || []
      );
      setImages(images);
      setError(null);
    } catch {
      setError('Failed to load slideshows. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load slideshows on component mount
  useEffect(() => {
    fetchSlideshows();
  }, []);

  // Handlers for slideshow operations
  const handleAddCategory = async (newSlideshow) => {
    try {
      const id = uuid();
      const indexOrder = Object.keys(slideshows).length;
      const items = {};
      const finalSlideshow = {
        id: id,
        indexOrder: indexOrder,
        items: items,
        ...newSlideshow,
      };
      const categories = Object.values(slideshows).concat([finalSlideshow]);
      await handleUpdateCategories(categories);
    } catch {
      setError('Failed to add slideshow. Please try again.');
    }
  };

  const handleUpdateCategories = async (categories) => {
    try {
      const obj = {
        group: 'slideshows',
        key: 'all',
        version: slideshowData.version,
        value: { items: {} },
      };
      const update = categories.reduce((acc, category) => {
        acc.value.items[category.id] = category;
        return acc;
      }, obj);
      await updateSlideshow(update);
      fetchSlideshows(); // Refresh the list
    } catch {
      setError('Failed to update slideshow. Please try again.');
    }
  };

  const handleSaveCategory = async (updatedSlideshow) => {
    try {
      const obj = {
        group: 'slideshows',
        key: 'all',
        version: slideshowData.version,
        value: { items: {} },
      };
      const update = Object.values(slideshows).reduce((acc, category) => {
        acc.value.items[category.id] = category;
        if (category.id === updatedSlideshow.id) {
          acc.value.items[category.id] = updatedSlideshow;
        }
        return acc;
      }, obj);
      await updateSlideshow(update);
      fetchSlideshows(); // Refresh the list
    } catch {
      setError('Failed to update slideshow. Please try again.');
    }
  };

  const handleDeleteCategory = async (slideshowId) => {
    try {
      const obj = {
        group: 'slideshows',
        key: 'all',
        version: slideshowData.version,
        value: { items: {} },
      };
      const update = Object.values(slideshows).reduce((acc, category) => {
        if (category.id !== slideshowId) {
          acc.value.items[category.id] = category;
        }
        return acc;
      }, obj);
      await updateSlideshow(update);
      fetchSlideshows(); // Refresh the list
    } catch {
      setError('Failed to delete slideshow. Please try again.');
    }
  };

  // Handler for opening the slideshow modal
  const handleManageCategories = () => {
    setShowModal(true);
  };

  // Handler for opening the add image modal
  const handleEditEntry = (categoryId, entryId) => {
    if (entryId) {
      // Edit existing image
      setSelectedImage(entryId);
      setSelectedSlideshow(categoryId);
      setImageEditMode('edit');
    } else {
      // Add new image
      setSelectedImage(null);
      setSelectedSlideshow(categoryId);
      setImageEditMode('add');
    }
    setShowImageModal(true);
  };

  const handleAddEntry = () => {
    handleEditEntry(null, null);
  };

  // Handler for closing the image modal
  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
    setSelectedSlideshow(null);
  };

  // Handler for saving an image
  const handleSaveImage = async (formData, slideshowId, andClose) => {
    try {
      // Create form data for file upload
      const uploadData = new FormData();
      if (formData.file) {
        uploadData.append('assetData', formData.file);
      }
      const id = formData.id || uuid(); //generate uuid
      const fileName = `${id}logo.jpg`;
      const nextIndexOrder = Object.keys(slideshows[slideshowId]?.items || {}).length || 0;
      const updateData = {
        ...slideshowData,
        asset: {
          group: 'pluginData/slideshows',
          id: fileName,
          type: 'file',
        },
        overwrite: true,
        value: {
          items: {
            ...slideshows,
            [slideshowId]: {
              ...slideshows[slideshowId],
              items: {
                ...slideshows[slideshowId].items,
                [id]: {
                  categoryId: slideshowId,
                  id: id,
                  img: `cms://${id}logo.jpg`,
                  indexOrder: nextIndexOrder,
                },
              },
            },
          },
        },
      };
      delete updateData.data;
      uploadData.append('data', JSON.stringify(updateData));

      if (formData.id) {
        uploadData.append('id', formData.id);
      }
      await addImage(uploadData);

      // Refresh the slideshows
      fetchSlideshows();

      if (andClose) {
        setShowImageModal(false);
      }
    } catch {
      setError('Failed to save image. Please try again.');
    }
  };

  // Handler for deleting an image
  const handleDeleteImage = async (imageId, slideshowId) => {
    try {
      const updateData = {
        ...slideshowData,
        overwrite: true,
        value: { items: slideshows },
      };
      delete updateData.data;
      delete updateData.value.items[slideshowId].items[imageId];
      await updateSlideshow(updateData);
      fetchSlideshows();
      setShowImageModal(false);
    } catch {
      setError('Failed to delete image. Please try again.');
    }
  };

  // Helper functions for image URLs
  const getEntryLogoPreviewUrl = (entryId) => {
    // Replace with actual URL
    return `/assets/images/slideshows/${entryId}`;
  };

  const getDefaultLogoPreviewUrl = (assetId) => {
    // Replace with actual URL
    return `/assets/images/default/${assetId || 'placeholder.png'}`;
  };

  const generateCmsUrl = (path) => {
    // Replace with actual URL generator
    return `/assets/${path}`;
  };

  const getEntryLogoName = (entryId) => {
    // Replace with actual file name generator
    return `slideshows/${entryId}`;
  };

  // Custom section components for DirectoryList

  const renderSection2 = () => <div className="col-xs-4"></div>;

  const renderSection3 = () => <div className="col-xs-3"></div>;

  return (
    <>
      <DirectoryList
        pageTitle="Slideshows"
        hideCategories={false}
        pluralEntriesTitle="Images"
        singleCategoryTitle="Slideshow"
        pluralCategoriesTitle="Slideshows"
        singleEntryTitle="Image"
        moduleColor="module-primary"
        categories={Object.values(slideshows).sort((a, b) =>
          (a?.title || '').localeCompare(b?.title || '')
        )}
        entryListArray={images}
        directoryListSection2Component={renderSection2}
        directoryListSection3Component={renderSection3}
        entriesOrderBy="indexOrder"
        onEditEntry={handleEditEntry}
        onManageCategories={handleManageCategories}
        onEntrySave={handleAddEntry}
        updateCategories={handleUpdateCategories}
        errorText={error}
        loading={loading}
      />

      {/* Slideshows Management Modal */}
      <ManageSlideshowsModal
        show={showModal}
        onHide={() => setShowModal(false)}
        moduleColor="module-primary"
        pluralCategoriesTitle="Slideshows"
        singleCategoryTitle="Slideshow"
        slideshows={slideshows}
        onSaveCategory={handleSaveCategory}
        onDeleteCategory={handleDeleteCategory}
        onAddCategory={handleAddCategory}
        categoriesOrderBy="title"
      />

      {/* Add/Edit Image Modal */}
      <AddImageModal
        show={showImageModal}
        onHide={handleCloseImageModal}
        moduleColor="module-primary"
        singleEntryTitle="Image"
        singleCategoryTitle="Slideshow"
        modeLabel={imageEditMode === 'add' ? 'Add' : 'Edit'}
        replaceMode={imageEditMode === 'edit'}
        hideCategories={!!selectedSlideshow}
        categories={Object.values(slideshows).sort((a, b) =>
          (a?.title || '').localeCompare(b?.title || '')
        )}
        categoriesOrderBy="title"
        entryId={selectedImage}
        entry={selectedImage ? images.find((img) => img.id === selectedImage) : {}}
        defaultEntryImgAssetId=""
        imgCategoryId={selectedSlideshow}
        onSave={handleSaveImage}
        onDelete={handleDeleteImage}
        getEntryLogoPreviewUrl={getEntryLogoPreviewUrl}
        getDefaultLogoPreviewUrl={getDefaultLogoPreviewUrl}
        generateCmsUrl={generateCmsUrl}
        getEntryLogoName={getEntryLogoName}
      />
    </>
  );
};

export default SlideShowListPage;
